import React, { useContext, createContext, useState, useEffect } from "react";
import {
  defaultTripDetailsInfo,
  ITripDetails,
  ITripDetailsInfo,
  TripDetailsInfo,
} from "../components/ServiceUsers/Interfaces/ITripDetails";
import {
  getAttributesByOrgId,
  getLookupListByListName,
  getWandaTripWithPagination,
} from "../services";
import { useTripsCtx } from "./TripDetailsContext";
import { notification } from "antd";
import { WandaTripStatus } from "../types/ApplicationTypes";
import moment from "moment";
import { getSubscriptionDetails } from "../services/PaymentService/PaymentSubscriptionService";
import {
  CancelSubscriptionReasons,
  CurrentSubscriptionList,
  LookupListItems,
  PaymentSubscriptionStatus,
  SubscriptionType,
} from "../types/SubscriptionTypes";
import { useLocation } from "react-router-dom";
import { usePersonSummaryCtx } from "./PersonSummaryContext";

export const PlanDetailsContext = createContext<any | undefined>(undefined);

export const usePlanDetailsCtx = () => useContext(PlanDetailsContext);

export const PlanDetailsProvider: React.FC = ({ children }) => {
  const [currentPlanDetails, setCurrentPlanDetails] = useState<
    CurrentSubscriptionList
  >();

  const [currentPlan, setCurrentPlan] = useState<string>("");
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [tripsAfterSubscriptionDate, setTripsAfterSubscriptionDate] = useState<
    ITripDetails[]
  >([]);
  const [isWelcomeWandaPlusVisible, setIsWelcomeWandaPlusVisible] = useState<
    boolean
  >(false);
  const [tripDetailsInfo, setTripDetailsInfo] = useState<ITripDetailsInfo>(
    defaultTripDetailsInfo
  );
  const [
    subscriptionCancellationReasons,
    setSubscriptionCancellationReasons,
  ] = useState<LookupListItems[]>();
  const [isPlusPlanExpired, setIsPlusPlanExpired] = useState<boolean>(false);

  const { pathname } = useLocation();

  const { wandaOrgId } = useTripsCtx();
  const {
    caregiverSummaryInfo,
    isLoadingPersonData,
    customerCareDetails,
  } = usePersonSummaryCtx();

  useEffect(() => {
    if (wandaOrgId.privatePayId) {
      GetOrganizationAttributes();
    }
  }, [wandaOrgId.privatePayId]);

  const fetchTripsAfterSubscriptionDate = async (validUntil: string) => {
    try {
      const apiResponse = await getWandaTripWithPagination({
        personId: caregiverSummaryInfo.personId,
        wandaTripStatusIds: WandaTripStatus.Booked,
        skipPagination: true,
      });

      const filteredTrips = apiResponse.personTrips.filter(
        (trip: { tripDate: moment.MomentInput }) =>
          moment(trip.tripDate).isAfter(moment(validUntil))
      );

      setTripsAfterSubscriptionDate(filteredTrips);
    } catch (error: any) {
      notification.error({
        message: `Error ${error.response.status}`,
        description: "Failed to Fetch Trips After Renewal",
      });
    }
  };

  const handlePlusPlan = async (planDetails: CurrentSubscriptionList) => {
    setSelectedPlan("Plus");
    setCurrentPlan("Plus");

    fetchTripsAfterSubscriptionDate(planDetails.validUntil);

    try {
      const apiResponse = await getLookupListByListName(
        1,
        CancelSubscriptionReasons.PaymentSubscriptionCancellationReason
      );
      const reasons = apiResponse.map((response: any) => ({
        value: response.itemId,
        label: response.itemName,
      }));
      setSubscriptionCancellationReasons(reasons);
    } catch (error) {
      notification.error({
        message: "Failed to Fetch Subscription Cancellation Reasons",
      });
    }

    const showModal = localStorage.getItem("showModal") === "true";
    if (showModal) {
      setIsWelcomeWandaPlusVisible(true);
      localStorage.removeItem("showModal");
    }
  };

  const handleBasicPlan = () => {
    setSelectedPlan("Basic");
    setCurrentPlan("Basic");
  };

  const handleCancelledPlusPlan = (expiresOn: string) => {
    const formattedCurrentDate = moment().format("MM/DD/YYYY");
    const formattedExpiryDate = moment(expiresOn).format("MM/DD/YYYY");

    const currentDate = moment(formattedCurrentDate);
    const expiryDate = moment(formattedExpiryDate);

    if (currentDate.isBefore(expiryDate)) {
      setSelectedPlan("Plus");
      setCurrentPlan("Plus");
    } else {
      handleBasicPlan();
      setIsPlusPlanExpired(true);
    }
  };

  const getCurrentSubscriptionPlanDetails = async () => {
    try {
      const planDetails = await getSubscriptionDetails({
        personId: caregiverSummaryInfo.personId,
        subscriptionTypeId: SubscriptionType.WandaPlus,
      });

      const currentPlanDetails = planDetails[0];
      setCurrentPlanDetails(currentPlanDetails);

      if (planDetails?.length > 0) {
        currentPlanDetails.paymentSubscriptionStatusId ===
        PaymentSubscriptionStatus.Active
          ? handlePlusPlan(currentPlanDetails)
          : handleCancelledPlusPlan(currentPlanDetails.expiresOn);
      } else {
        handleBasicPlan();
      }

      return planDetails;
    } catch (error: any) {
      notification.error({
        message: `Error ${error?.response?.status}`,
        description: error?.response?.statusText,
      });
    }
  };

  useEffect(() => {
    !isLoadingPersonData &&
      !["/Welcome"].includes(pathname) &&
      getCurrentSubscriptionPlanDetails();
  }, [isLoadingPersonData]);

  const GetOrganizationAttributes = async () => {
    try {
      const tripAttributes = await getAttributesByOrgId(
        wandaOrgId.privatePayId
      );
      
      let subscriptionAttribute = tripAttributes.find(
        (attributes: { attributeName: string }) =>
          attributes.attributeName === TripDetailsInfo.WandaPrivatePay
      );
      const subscriptionData =
        subscriptionAttribute &&
        JSON.parse(subscriptionAttribute.attributeValue);
      let discountAttribute = tripAttributes.find(
        (x: { attributeName: string }) =>
          x.attributeName === TripDetailsInfo.WandaPlusDiscount
      );
      const data = {
        wandaCustomerCareNumber: customerCareDetails.wandaCustomerCareNumber,
        wandaPlusDiscount: discountAttribute
          ? discountAttribute.attributeValue
          : "",
        wandaSubscriptionCost: subscriptionData.WandaPrivatePay.subscriptionCost.toString(),
      };
      setTripDetailsInfo(data);
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "An error occurred during fetching the Trips related info.",
      });
    }
  };

  return (
    <PlanDetailsContext.Provider
      value={{
        selectedPlanCtx: [selectedPlan, setSelectedPlan],
        welcomeWandaPlusModalCtx: [
          isWelcomeWandaPlusVisible,
          setIsWelcomeWandaPlusVisible,
        ],
        currentPlan,
        tripDetailsInfo,
        currentPlanDetails,
        isPlusPlanExpired,
        tripsAfterSubscriptionDateCtx: [
          tripsAfterSubscriptionDate,
          setTripsAfterSubscriptionDate,
        ],
        subscriptionCancellationReasons,
        getCurrentSubscriptionPlanDetails,
        fetchTripsAfterSubscriptionDate,
      }}
    >
      {children}
    </PlanDetailsContext.Provider>
  );
};
